/**
 * Pure H OS · Workstream 02 · Product Master
 * ===========================================
 * Single source of truth for every ORVAN product.
 *
 *  - List view with filters (lifecycle · launch · gender · collection · season · q)
 *  - Detail drawer with 6 tabs: Identity · Brand · Formula · Pricing ·
 *    Classification · AI Knowledge
 *  - "New Product" quick-create modal (code + name minimum)
 *  - Product status checklist strip
 *  - AI Knowledge tab is READ-ONLY placeholder — future Product-Bible
 *    ingestion will populate it (no inline generation).
 *
 * Every interactive element carries a data-testid.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package, Palette, FlaskConical, DollarSign, Layers, Sparkles,
  Plus, Save, Trash2, Search, RefreshCcw, ShieldCheck, CheckCircle2,
  XCircle, Info, AlertTriangle,
  Activity, Clock, Image as ImageIcon, FileText, History, ClipboardCheck,
  Download, ChevronDown, Printer, Globe,
} from "lucide-react";
import { Card, SectionHeader, Button, EmptyState, ScoreDial } from "../../components/lib";
import { api, productMaster, PMListItem, PMProduct, PMEnumMap, ApiError } from "../../services/api";
import { ReadinessTab, TimelineTab, AssetsTab, DocumentsTab, RevisionsTab, ApprovalTab } from "./WorkspaceTabs";
import MarketCoveragePanel from "./MarketCoveragePanel";

// ── Helpers ──────────────────────────────────────────────────────
function humanEnum(enums: PMEnumMap | null, key: keyof PMEnumMap, val?: string | null): string {
  if (!val) return "—";
  return enums?.[key]?.labels?.[val] ?? val;
}

function Field({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: 1 | 2 }) {
  return (
    <label className="flex flex-col gap-1" style={{ gridColumn: `span ${span} / span ${span}` }}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function TextIn({ value, onChange, testId, type = "text", dir, rows }: {
  value: string; onChange: (v: string) => void; testId: string;
  type?: string; dir?: "rtl" | "ltr"; rows?: number;
}) {
  const cls = "px-3 py-2 text-sm border rounded";
  const style = { borderColor: "var(--ph-border-strong)" } as const;
  if (rows) {
    return <textarea rows={rows} data-testid={testId} value={value} dir={dir}
      onChange={(e) => onChange(e.target.value)} className={cls} style={style} />;
  }
  return <input type={type} data-testid={testId} value={value} dir={dir}
    onChange={(e) => onChange(e.target.value)} className={cls} style={style} />;
}

function EnumSelect({ enums, k, value, onChange, testId, allowEmpty = true }: {
  enums: PMEnumMap | null; k: keyof PMEnumMap; value: string | null | undefined;
  onChange: (v: string | null) => void; testId: string; allowEmpty?: boolean;
}) {
  const opts = enums?.[k]?.values ?? [];
  const labels = enums?.[k]?.labels ?? {};
  return (
    <select
      className="px-3 py-2 text-sm border rounded"
      style={{ borderColor: "var(--ph-border-strong)" }}
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      {allowEmpty && <option value="">All</option>}
      {opts.map((o) => <option key={o} value={o}>{labels[o] ?? o}</option>)}
    </select>
  );
}

function Pill({ tone = "neutral", children, testId }: { tone?: "neutral" | "success" | "warning" | "danger" | "info" | "gold"; children: React.ReactNode; testId?: string }) {
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
    gold:    { bg: "var(--ph-warning-tint)", fg: "var(--ph-brand-primary)" },
  }[tone];
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded"
      style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}
    >{children}</span>
  );
}

function launchTone(status: string): "neutral" | "info" | "success" | "gold" | "warning" | "danger" {
  return ({
    idea: "neutral", formula_ready: "info", sampling: "info", packaging: "warning",
    ready_to_sell: "gold", active: "success", discontinued: "danger",
  } as any)[status] || "neutral";
}

// ── Approved Formula card — Product Master → R&D binding surface ──
function ApprovedFormulaCard({ product }: { product: any }) {
  const nav = useNavigate();
  const f = product?.formula || {};
  const hasFormula = !!f.formulaId;
  const state = hasFormula
    ? (f.isLocked ? "approved" : "linked")
    : "no_formula";
  const stateLabel = {
    approved:    "Approved & locked",
    linked:      "Linked · not locked",
    no_formula:  "No formula yet",
  }[state];
  const stateTone: "gold" | "info" | "neutral" =
    state === "approved" ? "gold" : (state === "linked" ? "info" : "neutral");

  const openInRD = () => {
    nav(`/pureh/rd-lab?productId=${encodeURIComponent(product.id)}`);
  };

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 rounded mb-4"
      style={{
        background: "linear-gradient(90deg, rgba(176,141,61,0.06), rgba(37,35,15,0.03))",
        border: "1px solid var(--ph-border-subtle)",
      }}
      data-testid="approved-formula-card"
    >
      <FlaskConical size={18} className="opacity-60 shrink-0" />
      <div className="flex-1 grid grid-cols-4 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Current Approved Formula</div>
          <div className="text-sm font-bold text-slate-900 mt-0.5 font-mono" data-testid="afc-code">
            {f.formulaCode || (hasFormula ? f.formulaId : "—")}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Formula Version</div>
          <div className="text-sm font-bold text-slate-900 mt-0.5" data-testid="afc-version">
            {f.version ? `v${f.version}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Formula Status</div>
          <div className="mt-0.5">
            <Pill tone={stateTone} testId="afc-status">{stateLabel}</Pill>
          </div>
        </div>
        <div className="flex items-center justify-end">
          <Button variant={hasFormula ? "primary" : "ghost"} onClick={openInRD} testId="afc-open-rd">
            <FlaskConical size={13} />
            {hasFormula ? "Open in R&D" : "Create formula in R&D"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Export dropdown ──────────────────────────────────────────────
const EXPORT_ITEMS: {
  key: string; label: string; description: string;
  kind: "profile/pdf" | "profile/docx" | "profile/pptx" | "sales/pdf" | "technical/pdf" | "marketing/pdf" | "print" | "labels-single" | "labels-sheet";
}[] = [
  { key: "profile-pdf",   label: "Product Profile · PDF",   description: "10-page luxury dossier",                kind: "profile/pdf" },
  { key: "profile-docx",  label: "Product Profile · DOCX",  description: "Editable Word document",                kind: "profile/docx" },
  { key: "profile-pptx",  label: "Investor Deck · PPTX",    description: "Condensed 5-slide presentation",        kind: "profile/pptx" },
  { key: "sales-pdf",     label: "Sales Sheet · PDF",       description: "2-page commercial one-pager",           kind: "sales/pdf" },
  { key: "technical-pdf", label: "Technical Sheet · PDF",   description: "Manufacturing & compliance",            kind: "technical/pdf" },
  { key: "marketing-pdf", label: "Marketing Sheet · PDF",   description: "Creative brief · PR & social",          kind: "marketing/pdf" },
  { key: "label-single",  label: "Barcode Label · Single",  description: "ليبل مفرد 60×35mm · EAN-13",            kind: "labels-single" },
  { key: "label-sheet",   label: "Barcode Labels · A4 Sheet", description: "ورقة A4 · 24 ليبل لكل صفحة",          kind: "labels-sheet" },
  { key: "print",         label: "Print · Product Profile", description: "Open PDF in a printable window",        kind: "print" },
];

function ExportMenu({ ventureId, productId, productCode }: {
  ventureId: string; productId: string; productCode: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3200);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target?.closest?.("[data-export-menu]")) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const endpoint = (kind: string) =>
    kind.startsWith("labels-")
      ? `${process.env.REACT_APP_BACKEND_URL}/api/pureh/product-master/ventures/${ventureId}/products/${productId}/labels/pdf?mode=${kind === "labels-sheet" ? "sheet" : "single"}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/pureh/product-master/ventures/${ventureId}/products/${productId}/export/${kind}`;

  const extFromKind = (kind: string) => {
    if (kind.endsWith("docx")) return "docx";
    if (kind.endsWith("pptx")) return "pptx";
    return "pdf";
  };

  const handleExport = async (item: typeof EXPORT_ITEMS[number]) => {
    setBusy(item.key);
    setOpen(false);
    try {
      if (item.kind === "print") {
        const win = window.open(endpoint("profile/pdf"), "_blank", "noopener");
        setTimeout(() => { try { win?.print?.(); } catch { /* pop-up blocker */ } }, 1200);
        setFlash("Opened printable Product Profile.");
        return;
      }
      const res = await fetch(endpoint(item.kind), { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const kindSlug = item.kind.replace("/", "-");
      const ext = extFromKind(item.kind);
      a.download = `${(productCode || "product").toLowerCase()}-${kindSlug.replace(`-${ext}`, "")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setFlash(`${item.label} downloaded.`);
    } catch (e: any) {
      setFlash(e?.message || "Export failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative" data-export-menu>
      <Button
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        testId="export-btn"
      >
        <Download size={14} />
        {busy ? "Preparing…" : "Export"}
        <ChevronDown size={12} />
      </Button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-40 rounded shadow-lg overflow-hidden"
          style={{
            minWidth: 320,
            background: "var(--ph-surface)",
            border: "1px solid var(--ph-border-strong)",
          }}
          data-testid="export-menu"
        >
          <div
            className="px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ph-ink-muted)", borderBottom: "1px solid var(--ph-border-subtle)", background: "var(--ph-surface-alt)" }}
          >
            Export · ORVAN Product Profile
          </div>
          <div className="py-1">
            {EXPORT_ITEMS.map((it) => {
              const Icon = it.kind === "print" ? Printer : it.kind.endsWith("docx")
                ? FileText : it.kind.endsWith("pptx") ? Layers : Download;
              return (
                <button
                  key={it.key}
                  onClick={() => handleExport(it)}
                  className="w-full text-left px-4 py-2 hover:opacity-80 flex items-start gap-3 transition"
                  style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}
                  data-testid={`export-item-${it.key}`}
                >
                  <Icon size={14} className="mt-1 opacity-60" />
                  <div className="flex-1">
                    <div className="text-sm font-medium" style={{ color: "var(--ph-ink)" }}>{it.label}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>{it.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {flash && (
        <div
          className="absolute right-0 top-full mt-2 z-50 px-4 py-2 rounded shadow"
          style={{
            background: "var(--ph-brand-primary, #1a1a1a)",
            color: "#F7F3EB",
            fontSize: 12,
            letterSpacing: "0.02em",
          }}
          data-testid="export-flash"
        >
          {flash}
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
type TabKey = "identity" | "brand" | "formula" | "pricing" | "markets" | "classification" | "knowledge"
            | "readiness" | "timeline" | "assets" | "documents" | "revisions" | "approval";
const TABS: { key: TabKey; label: string; Icon: any }[] = [
  { key: "identity",       label: "Identity",       Icon: Package },
  { key: "brand",          label: "Brand",          Icon: Palette },
  { key: "formula",        label: "Formula",        Icon: FlaskConical },
  { key: "pricing",        label: "Pricing",        Icon: DollarSign },
  { key: "markets",        label: "Markets",        Icon: Globe },
  { key: "classification", label: "Classification", Icon: Layers },
  { key: "knowledge",      label: "AI Knowledge",   Icon: Sparkles },
  { key: "readiness",      label: "Readiness",      Icon: Activity },
  { key: "timeline",       label: "Timeline",       Icon: Clock },
  { key: "assets",         label: "Assets",         Icon: ImageIcon },
  { key: "documents",      label: "Documents",      Icon: FileText },
  { key: "revisions",      label: "Revisions",      Icon: History },
  { key: "approval",       label: "Approval",       Icon: ClipboardCheck },
];

export default function ProductMasterLayout() {
  const nav = useNavigate();
  const [ventures, setVentures] = useState<Array<{ id: string; name: string }>>([]);
  const [ventureId, setVentureId] = useState<string>("orvan");
  const [enums, setEnums] = useState<PMEnumMap | null>(null);
  const [rows, setRows] = useState<PMListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<{ q?: string; lifecycle?: string; launchStatus?: string; gender?: string; collection?: string; season?: string }>({});
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PMProduct | null>(null);
  const [tab, setTab] = useState<TabKey>("identity");
  const [dirty, setDirty] = useState<Record<string, any>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Boot: ventures + enums
  useEffect(() => {
    (async () => {
      try {
        const [vs, en] = await Promise.all([api.ventures(), productMaster.enums()]);
        const list = (vs as any[]).map((v) => ({ id: v.id, name: v.name || v.brand || v.id }));
        setVentures(list);
        const orvan = list.find((v) => v.id === "orvan");
        setVentureId(orvan?.id || list[0]?.id || "orvan");
        setEnums(en);
      } catch {
        setVentures([{ id: "orvan", name: "ORVAN Perfumes" }]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await productMaster.list(ventureId, { ...filters, includeArchived: includeArchived ? "true" : undefined });
      setRows(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [ventureId, filters, includeArchived]);
  useEffect(() => { if (ventureId) load(); }, [ventureId, load]);

  const openProduct = async (id: string) => {
    setSelectedId(id); setTab("identity"); setDirty({});
    try { const p = await productMaster.get(ventureId, id); setSelected(p); }
    catch { setSelected(null); }
  };

  const showFlash = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); };

  const setField = (path: string, value: any) => {
    setDirty((d) => {
      if (!path.includes(".")) return { ...d, [path]: value };
      const [k, sub] = path.split(".");
      return { ...d, [k]: { ...(d[k] ?? {}), [sub]: value } };
    });
  };

  const getField = (path: string): any => {
    if (path.includes(".")) {
      const [k, sub] = path.split(".");
      if (dirty[k] && sub in dirty[k]) return dirty[k][sub];
      return (selected?.[k] ?? {})?.[sub];
    }
    return (path in dirty) ? dirty[path] : selected?.[path];
  };

  const save = async () => {
    if (!selectedId || !Object.keys(dirty).length) return;
    try {
      const updated = await productMaster.patch(ventureId, selectedId, dirty);
      setSelected(updated); setDirty({});
      await load();
      showFlash("Product saved.");
    } catch (e) {
      showFlash(e instanceof Error ? e.message : "Save failed.");
    }
  };

  const softDelete = async () => {
    if (!selectedId) return;
    if (!confirm("Soft-delete this product? It will be marked discontinued.")) return;
    try {
      await productMaster.softDelete(ventureId, selectedId);
      setSelected(null); setSelectedId(null);
      await load();
      showFlash("Product discontinued.");
    } catch (e) {
      showFlash(e instanceof Error ? e.message : "Delete failed.");
    }
  };

  const filteredCount = rows.length;

  return (
    <div data-testid="product-master-root">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Workstream 02</div>
          <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "var(--ph-display-font)" }} data-testid="page-title">
            Product Master
          </h1>
          <div className="text-sm text-slate-500 mt-1">Single source of truth for every ORVAN product. All downstream modules consume this.</div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Venture</label>
          <select
            value={ventureId}
            onChange={(e) => { setVentureId(e.target.value); setSelected(null); setSelectedId(null); }}
            className="px-3 py-2 text-sm border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="venture-select"
          >
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <Button variant="secondary" onClick={load} testId="refresh-btn"><RefreshCcw size={14} /> Refresh</Button>
          <Button variant="secondary" onClick={() => nav("/pureh/product-master/knowledge-import")} testId="knowledge-import-btn">
            <Sparkles size={14} /> Knowledge Import
          </Button>
          <Button variant="primary" onClick={() => setShowCreate(true)} testId="new-product-btn"><Plus size={14} /> New Product</Button>
        </div>
      </div>

      {/* Layout: list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        {/* List column */}
        <Card>
          {/* Filters */}
          <div className="flex flex-col gap-2 mb-4">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="filter-q"
                placeholder="Search code or name…"
                value={filters.q ?? ""}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="w-full pl-8 pr-3 py-2 text-sm border rounded"
                style={{ borderColor: "var(--ph-border-strong)" }}
              />
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="pm-filters">
              <label className="flex flex-col text-[10px] font-semibold uppercase tracking-widest text-slate-500">Lifecycle
                <EnumSelect enums={enums} k="lifecycle"    value={filters.lifecycle    ?? null} onChange={(v) => setFilters({ ...filters, lifecycle:    v ?? undefined })} testId="filter-lifecycle" />
              </label>
              <label className="flex flex-col text-[10px] font-semibold uppercase tracking-widest text-slate-500">Launch status
                <EnumSelect enums={enums} k="launchStatus" value={filters.launchStatus ?? null} onChange={(v) => setFilters({ ...filters, launchStatus: v ?? undefined })} testId="filter-launch" />
              </label>
              <label className="flex flex-col text-[10px] font-semibold uppercase tracking-widest text-slate-500">Gender
                <EnumSelect enums={enums} k="gender"       value={filters.gender       ?? null} onChange={(v) => setFilters({ ...filters, gender:       v ?? undefined })} testId="filter-gender" />
              </label>
              <label className="flex flex-col text-[10px] font-semibold uppercase tracking-widest text-slate-500">Collection
                <EnumSelect enums={enums} k="collection"   value={filters.collection   ?? null} onChange={(v) => setFilters({ ...filters, collection:   v ?? undefined })} testId="filter-collection" />
              </label>
              <label className="flex flex-col text-[10px] font-semibold uppercase tracking-widest text-slate-500">Season
                <EnumSelect enums={enums} k="season"       value={filters.season       ?? null} onChange={(v) => setFilters({ ...filters, season:       v ?? undefined })} testId="filter-season" />
              </label>
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer text-slate-600">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                data-testid="filter-includeArchived"
              />
              Show archived
            </label>
          </div>

          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
            {loading ? "Loading…" : `${filteredCount} product${filteredCount === 1 ? "" : "s"}`}
          </div>

          <div className="space-y-1 max-h-[70vh] overflow-y-auto -mx-2 px-2">
            {rows.length === 0 && !loading && <EmptyState title="No products" hint="Adjust filters or create a new product." />}
            {rows.map((p) => (
              <button
                key={p.id}
                onClick={() => openProduct(p.id)}
                data-testid={`product-row-${p.productCode}`}
                className="w-full text-left rounded p-3 border transition-colors"
                style={{
                  borderColor: selectedId === p.id ? "var(--ph-brand-primary)" : "var(--ph-border-subtle)",
                  background: selectedId === p.id ? "var(--ph-warning-tint)" : "transparent",
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: "var(--ph-display-font)" }}>
                      {p.productCode}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{p.nameEn}</div>
                  </div>
                  <Pill tone={launchTone(p.launchStatus)}>{humanEnum(enums, "launchStatus", p.launchStatus)}</Pill>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {p.gender && <Pill tone="info">{humanEnum(enums, "gender", p.gender)}</Pill>}
                  {p.concentration && <Pill tone="info">{(p as any).concentrationLabel || humanEnum(enums, "concentration", p.concentration)}</Pill>}
                  {p.season && <Pill tone="neutral">{humanEnum(enums, "season", p.season)}</Pill>}
                  {p.priceTier && <Pill tone="gold">{humanEnum(enums, "priceTier", p.priceTier)}</Pill>}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Detail column */}
        <div>
          {!selected ? (
            <Card>
              <EmptyState title="No product selected" hint="Pick a product from the list to view its Master record." />
            </Card>
          ) : (
            <ProductDetail
              enums={enums}
              product={selected}
              dirty={dirty}
              getField={getField}
              setField={setField}
              tab={tab}
              setTab={setTab}
              onSave={save}
              onDelete={softDelete}
              hasChanges={Object.keys(dirty).length > 0}
              onReload={async () => { if (selectedId) { const p = await productMaster.get(ventureId, selectedId); setSelected(p); await load(); } }}
            />
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateProductModal
          ventureId={ventureId}
          onClose={() => setShowCreate(false)}
          onCreated={async (id) => { setShowCreate(false); await load(); await openProduct(id); showFlash("Product created."); }}
        />
      )}

      {flash && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-slate-900 text-white text-sm rounded shadow-lg z-50" data-testid="flash-toast">
          {flash}
        </div>
      )}
    </div>
  );
}

// ── Detail view ─────────────────────────────────────────────────
function ProductDetail({
  enums, product, dirty, getField, setField, tab, setTab, onSave, onDelete, hasChanges, onReload,
}: {
  enums: PMEnumMap | null;
  product: PMProduct;
  dirty: Record<string, any>;
  getField: (path: string) => any;
  setField: (path: string, v: any) => void;
  tab: TabKey; setTab: (t: TabKey) => void;
  onSave: () => void; onDelete: () => void;
  hasChanges: boolean;
  onReload?: () => void;
}) {
  const checklist = { ...(product.checklist || {}), ...(dirty.checklist || {}) };
  const renderCheckItem = (k: string, label: string) => (
    <label key={k} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
      <input
        type="checkbox"
        checked={!!checklist[k]}
        onChange={(e) => setField(`checklist.${k}`, e.target.checked)}
        data-testid={`checklist-${k}`}
      />
      {checklist[k] ? <CheckCircle2 size={12} className="text-green-600" /> : <XCircle size={12} className="text-slate-300" />}
      <span>{label}</span>
    </label>
  );

  return (
    <Card>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
            {product.brandName || "ORVAN"} · Product Master
          </div>
          <div className="flex items-center gap-3 mt-1">
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "var(--ph-display-font)" }} data-testid="detail-code">
              {product.productCode}
            </h2>
            <Pill tone={launchTone(product.launchStatus)} testId="detail-launch-status">
              {humanEnum(enums, "launchStatus", product.launchStatus)}
            </Pill>
            <Pill tone={product.lifecycle === "active" ? "success" : product.lifecycle === "discontinued" ? "danger" : "neutral"}>
              {humanEnum(enums, "lifecycle", product.lifecycle)}
            </Pill>
            {product.approval?.state && product.approval.state !== "draft" && (
              <Pill
                tone={
                  product.approval.state === "approved" || product.approval.state === "published" ? "success"
                  : product.approval.state === "rejected" ? "danger" : "warning"
                }
                testId="detail-approval-state"
              >
                {product.approval.state.replace("_", " ")}
              </Pill>
            )}
          </div>
          <div className="text-sm text-slate-600 mt-0.5">
            {product.nameEn}
            {product.nameAr ? <> · <bdi>{product.nameAr}</bdi></> : null}
            {(product as any).provenance?.concentration?.value ? <> · <bdi>{(product as any).provenance.concentration.value}</bdi></> : null}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {typeof product.readiness?.score === "number" && (
            <button
              onClick={() => setTab("readiness")}
              className="flex items-center gap-2 hover:opacity-80"
              data-testid="header-readiness-dial"
              title="Product readiness score"
            >
              <ScoreDial score={product.readiness.score} />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Readiness</div>
                <div className="text-lg font-bold text-slate-900">{product.readiness.score}<span className="text-xs text-slate-500">/100</span></div>
              </div>
            </button>
          )}
          <Button variant="ghost" onClick={onDelete} testId="delete-btn"><Trash2 size={14} /> Archive</Button>
          <ExportMenu
            ventureId={product.ventureId || "orvan"}
            productId={product.id}
            productCode={product.productCode || ""}
          />
          <Button variant="primary" onClick={onSave} disabled={!hasChanges} testId="save-btn">
            <Save size={14} /> {hasChanges ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Checklist strip */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 py-2 px-3 rounded mb-4"
        style={{ background: "var(--ph-surface-alt)", border: "1px solid var(--ph-border-subtle)" }}
        data-testid="checklist-strip">
        {renderCheckItem("formulaLocked",      "Formula locked")}
        {renderCheckItem("readyForShopify",    "Ready for Shopify")}
        {renderCheckItem("readyForMarketing",  "Ready for marketing")}
        {renderCheckItem("readyForProduction", "Ready for production")}
        {renderCheckItem("imagesUploaded",     "Images uploaded")}
        {renderCheckItem("pricingApproved",    "Pricing approved")}
        {renderCheckItem("packagingApproved",  "Packaging approved")}
        {renderCheckItem("complianceApproved", "Compliance approved")}
      </div>

      {/* Approved Formula card — surfaces R&D binding directly on Product Master */}
      <ApprovedFormulaCard product={product} />

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto mb-4" style={{ borderColor: "var(--ph-border-subtle)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap"
            style={{
              color: tab === t.key ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              borderBottom: tab === t.key ? "2px solid var(--ph-brand-primary)" : "2px solid transparent",
            }}
            data-testid={`tab-${t.key}`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div data-testid={`tab-content-${tab}`}>
        {tab === "identity" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product Code (immutable)">
              <input value={product.productCode} disabled data-testid="ro-productCode"
                className="px-3 py-2 text-sm border rounded bg-slate-50 text-slate-500"
                style={{ borderColor: "var(--ph-border-strong)" }} />
            </Field>
            <Field label="SKU">
              <TextIn value={getField("sku") ?? ""} onChange={(v) => setField("sku", v)} testId="field-sku" />
            </Field>
            <Field label="Name (English)">
              <TextIn value={getField("nameEn") ?? ""} onChange={(v) => setField("nameEn", v)} testId="field-nameEn" />
            </Field>
            <Field label="Name (Arabic)">
              <TextIn value={getField("nameAr") ?? ""} onChange={(v) => setField("nameAr", v)} testId="field-nameAr" dir="rtl" />
            </Field>
            <Field label="Family">
              <TextIn value={getField("family") ?? ""} onChange={(v) => setField("family", v)} testId="field-family" />
            </Field>
            <Field label="Line">
              <TextIn value={getField("line") ?? ""} onChange={(v) => setField("line", v)} testId="field-line" />
            </Field>
            <Field label="Bottle Size (ml)">
              <TextIn type="number" value={String(getField("bottleSizeMl") ?? "")} onChange={(v) => setField("bottleSizeMl", v === "" ? null : Number(v))} testId="field-bottleSizeMl" />
            </Field>
            <Field label="Packaging Size">
              <TextIn value={getField("packagingSize") ?? ""} onChange={(v) => setField("packagingSize", v)} testId="field-packagingSize" />
            </Field>
            <Field label="Gender">
              <EnumSelect enums={enums} k="gender" value={getField("gender")} onChange={(v) => setField("gender", v)} testId="field-gender" />
            </Field>
            <Field label="Concentration">
              <EnumSelect enums={enums} k="concentration" value={getField("concentration")} onChange={(v) => setField("concentration", v)} testId="field-concentration" />
            </Field>
            <Field label="Collection">
              <EnumSelect enums={enums} k="collection" value={getField("collection")} onChange={(v) => setField("collection", v)} testId="field-collection" />
            </Field>
            <Field label="Season">
              <EnumSelect enums={enums} k="season" value={getField("season")} onChange={(v) => setField("season", v)} testId="field-season" />
            </Field>
            <Field label="Lifecycle">
              <EnumSelect enums={enums} k="lifecycle" value={getField("lifecycle")} onChange={(v) => setField("lifecycle", v)} testId="field-lifecycle" allowEmpty={false} />
            </Field>
            <Field label="Launch Status">
              <EnumSelect enums={enums} k="launchStatus" value={getField("launchStatus")} onChange={(v) => setField("launchStatus", v)} testId="field-launchStatus" allowEmpty={false} />
            </Field>
            <Field label="Internal Notes" span={2}>
              <TextIn value={getField("internalNotes") ?? ""} onChange={(v) => setField("internalNotes", v)} testId="field-internalNotes" rows={3} />
            </Field>
          </div>
        )}

        {tab === "brand" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand Name">
                <TextIn value={getField("brandName") ?? ""} onChange={(v) => setField("brandName", v)} testId="field-brandName" />
              </Field>
              <Field label="Positioning">
                <EnumSelect enums={enums} k="positioning" value={getField("positioning")} onChange={(v) => setField("positioning", v)} testId="field-positioning" />
              </Field>
              <Field label="Hero Image (ref/URL)">
                <TextIn value={getField("heroImageRef") ?? ""} onChange={(v) => setField("heroImageRef", v)} testId="field-heroImageRef" />
              </Field>
              <Field label="Packshot Image (ref/URL)">
                <TextIn value={getField("packshotImageRef") ?? ""} onChange={(v) => setField("packshotImageRef", v)} testId="field-packshotImageRef" />
              </Field>
            </div>

            {/* Bilingual copy */}
            <SectionHeader eyebrow="Website & Marketing Copy" title="Bilingual (Arabic + English)" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Short Description (EN)"><TextIn rows={2} value={getField("copy.shortDescriptionEn") ?? ""} onChange={(v) => setField("copy.shortDescriptionEn", v)} testId="field-shortDescriptionEn" /></Field>
              <Field label="Short Description (AR)"><TextIn rows={2} value={getField("copy.shortDescriptionAr") ?? ""} onChange={(v) => setField("copy.shortDescriptionAr", v)} testId="field-shortDescriptionAr" dir="rtl" /></Field>
              <Field label="Long Story (EN)"><TextIn rows={4} value={getField("copy.longStoryEn") ?? ""} onChange={(v) => setField("copy.longStoryEn", v)} testId="field-longStoryEn" /></Field>
              <Field label="Long Story (AR)"><TextIn rows={4} value={getField("copy.longStoryAr") ?? ""} onChange={(v) => setField("copy.longStoryAr", v)} testId="field-longStoryAr" dir="rtl" /></Field>
              <Field label="Website Copy (EN)"><TextIn rows={4} value={getField("copy.websiteCopyEn") ?? ""} onChange={(v) => setField("copy.websiteCopyEn", v)} testId="field-websiteCopyEn" /></Field>
              <Field label="Website Copy (AR)"><TextIn rows={4} value={getField("copy.websiteCopyAr") ?? ""} onChange={(v) => setField("copy.websiteCopyAr", v)} testId="field-websiteCopyAr" dir="rtl" /></Field>
              <Field label="Feeling"><TextIn value={getField("copy.feeling") ?? ""} onChange={(v) => setField("copy.feeling", v)} testId="field-feeling" /></Field>
              <Field label="Who wears it"><TextIn value={getField("copy.whoWearsIt") ?? ""} onChange={(v) => setField("copy.whoWearsIt", v)} testId="field-whoWearsIt" /></Field>
              <Field label="When to wear it"><TextIn value={getField("copy.whenToWearIt") ?? ""} onChange={(v) => setField("copy.whenToWearIt", v)} testId="field-whenToWearIt" /></Field>
              <Field label="Feels like"><TextIn value={getField("copy.feelsLike") ?? ""} onChange={(v) => setField("copy.feelsLike", v)} testId="field-feelsLike" /></Field>
            </div>
          </div>
        )}

        {tab === "formula" && (
          <div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2 mb-3">
              <Info size={12} /> Formula reference only — the actual formula lives in the R&amp;D module and is linked here by ID.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Formula ID (set by R&D approval)">
                <input value={getField("formula.formulaId") ?? ""} disabled data-testid="field-formulaId"
                  className="px-3 py-2 text-sm border rounded bg-slate-50 text-slate-500"
                  style={{ borderColor: "var(--ph-border-strong)" }} />
              </Field>
              <Field label="Formula Code (set by R&D approval)">
                <input value={getField("formula.formulaCode") ?? ""} disabled data-testid="field-formulaCode"
                  className="px-3 py-2 text-sm border rounded bg-slate-50 text-slate-500"
                  style={{ borderColor: "var(--ph-border-strong)" }} />
              </Field>
              <Field label="Formula Version (set by R&D approval)">
                <input value={getField("formula.version") ?? ""} disabled data-testid="field-formulaVersion"
                  className="px-3 py-2 text-sm border rounded bg-slate-50 text-slate-500"
                  style={{ borderColor: "var(--ph-border-strong)" }} />
              </Field>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={!!getField("formula.isLocked")} onChange={(e) => setField("formula.isLocked", e.target.checked)} data-testid="field-formulaIsLocked" />
                <span>Formula is locked</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!getField("formula.ifraCompliant")} onChange={(e) => setField("formula.ifraCompliant", e.target.checked)} data-testid="field-ifraCompliant" />
                <span>IFRA compliant</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!getField("formula.allergensDeclared")} onChange={(e) => setField("formula.allergensDeclared", e.target.checked)} data-testid="field-allergensDeclared" />
                <span>Allergens declared</span>
              </label>
            </div>

            {/* Notes pyramid */}
            <SectionHeader eyebrow="Fragrance Notes" title="Top · Heart · Base" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Top notes (comma-sep)">
                <TextIn rows={3}
                  value={(getField("notes.top") ?? []).join(", ")}
                  onChange={(v) => setField("notes.top", v.split(",").map((s) => s.trim()).filter(Boolean))}
                  testId="field-notes-top" />
              </Field>
              <Field label="Heart notes (comma-sep)">
                <TextIn rows={3}
                  value={(getField("notes.heart") ?? []).join(", ")}
                  onChange={(v) => setField("notes.heart", v.split(",").map((s) => s.trim()).filter(Boolean))}
                  testId="field-notes-heart" />
              </Field>
              <Field label="Base notes (comma-sep)">
                <TextIn rows={3}
                  value={(getField("notes.base") ?? []).join(", ")}
                  onChange={(v) => setField("notes.base", v.split(",").map((s) => s.trim()).filter(Boolean))}
                  testId="field-notes-base" />
              </Field>
            </div>
          </div>
        )}

        {tab === "pricing" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost (EGP)"><TextIn type="number" value={String(getField("pricing.costEGP") ?? "")} onChange={(v) => setField("pricing.costEGP", v === "" ? null : Number(v))} testId="field-costEGP" /></Field>
            <Field label="Cost (SAR)"><TextIn type="number" value={String(getField("pricing.costSAR") ?? "")} onChange={(v) => setField("pricing.costSAR", v === "" ? null : Number(v))} testId="field-costSAR" /></Field>
            <Field label="List Price (EGP)"><TextIn type="number" value={String(getField("pricing.listPriceEGP") ?? "")} onChange={(v) => setField("pricing.listPriceEGP", v === "" ? null : Number(v))} testId="field-listPriceEGP" /></Field>
            <Field label="Wholesale Price (EGP)"><TextIn type="number" value={String(getField("pricing.wholesalePriceEGP") ?? "")} onChange={(v) => setField("pricing.wholesalePriceEGP", v === "" ? null : Number(v))} testId="field-wholesalePriceEGP" /></Field>
            <Field label="Wholesale Price (SAR)"><TextIn type="number" value={String(getField("pricing.wholesalePriceSAR") ?? "")} onChange={(v) => setField("pricing.wholesalePriceSAR", v === "" ? null : Number(v))} testId="field-wholesalePriceSAR" /></Field>
            <Field label="Retail Egypt (EGP) — سعر رفّ شامل الضريبة"><TextIn type="number" value={String(getField("pricing.retailPriceEGP") ?? "")} onChange={(v) => setField("pricing.retailPriceEGP", v === "" ? null : Number(v))} testId="field-retailPriceEGP" /></Field>
            <Field label="Retail Saudi (SAR) — سعر رفّ شامل الضريبة"><TextIn type="number" value={String(getField("pricing.retailPriceSAR") ?? "")} onChange={(v) => setField("pricing.retailPriceSAR", v === "" ? null : Number(v))} testId="field-retailPriceSAR" /></Field>
            <Field label="Export Price (USD)"><TextIn type="number" value={String(getField("pricing.exportPriceUSD") ?? "")} onChange={(v) => setField("pricing.exportPriceUSD", v === "" ? null : Number(v))} testId="field-exportPriceUSD" /></Field>
            <Field label="Margin Target (%)"><TextIn type="number" value={String(getField("pricing.marginTargetPct") ?? "")} onChange={(v) => setField("pricing.marginTargetPct", v === "" ? null : Number(v))} testId="field-marginTargetPct" /></Field>
            <Field label="Tax Code"><TextIn value={getField("pricing.taxCode") ?? ""} onChange={(v) => setField("pricing.taxCode", v)} testId="field-taxCode" /></Field>
            <Field label="Price Tier">
              <EnumSelect enums={enums} k="priceTier" value={getField("pricing.priceTier")} onChange={(v) => setField("pricing.priceTier", v)} testId="field-priceTier" />
            </Field>
          </div>
        )}

        {tab === "markets" && (
          <MarketCoveragePanel ventureId={product.ventureId || "orvan"} productId={product.id} />
        )}

        {tab === "classification" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Barcode (EAN/UPC)"><TextIn value={getField("classification.barcode") ?? ""} onChange={(v) => setField("classification.barcode", v)} testId="field-barcode" /></Field>
            <Field label="Unit of Sale"><TextIn value={getField("classification.unitOfSale") ?? ""} onChange={(v) => setField("classification.unitOfSale", v)} testId="field-unitOfSale" /></Field>
            <Field label="HS Code (Customs)"><TextIn value={getField("classification.hsCode") ?? ""} onChange={(v) => setField("classification.hsCode", v)} testId="field-hsCode" /></Field>
            <Field label="Industry Category"><TextIn value={getField("classification.industryCategory") ?? ""} onChange={(v) => setField("classification.industryCategory", v)} testId="field-industryCategory" /></Field>
            <Field label="Fragrance Family"><TextIn value={getField("classification.fragranceFamily") ?? ""} onChange={(v) => setField("classification.fragranceFamily", v)} testId="field-fragranceFamily" /></Field>
            <Field label="Target Audience"><TextIn value={getField("classification.targetAudience") ?? ""} onChange={(v) => setField("classification.targetAudience", v)} testId="field-targetAudience" /></Field>
          </div>
        )}

        {tab === "knowledge" && (
          <div>
            <div className="p-4 rounded mb-4"
              style={{ background: "var(--ph-surface-alt)", border: "1px dashed var(--ph-border-strong)" }}
              data-testid="knowledge-banner">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">AI Knowledge · Read-only</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    This section is populated by the Product Bible enrichment pipeline (not yet wired). No inline AI generation happens here — all fields below reflect the last stored snapshot.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ROField label="Product Bible"      value={product.aiKnowledge?.productBible} testId="ro-productBible" />
              <ROField label="Brand Story"        value={product.aiKnowledge?.brandStory}    testId="ro-brandStory" />
              <ROField label="Notes Description"  value={product.aiKnowledge?.notesDescription} testId="ro-notesDescription" />
              <ROField label="Marketing Angles"   value={(product.aiKnowledge?.marketingAngles || []).join(" · ")} testId="ro-marketingAngles" />
              <ROField label="Selling Points"     value={(product.aiKnowledge?.sellingPoints || []).join(" · ")}    testId="ro-sellingPoints" />
              <ROField label="SEO Keywords"       value={(product.aiKnowledge?.seoKeywords || []).join(", ")}       testId="ro-seoKeywords" />
              <ROField label="Last Enriched"      value={product.aiKnowledge?.lastEnrichedAt} testId="ro-lastEnrichedAt" />
              <ROField label="Enrichment Source"  value={product.aiKnowledge?.enrichmentSource} testId="ro-enrichmentSource" />
            </div>
          </div>
        )}

        {tab === "readiness" && (
          <ReadinessTab
            ventureId={product.ventureId}
            productId={product.id}
            initial={product.readiness}
            onRecomputed={onReload}
          />
        )}
        {tab === "timeline" && (
          <TimelineTab ventureId={product.ventureId} productId={product.id} />
        )}
        {tab === "assets" && (
          <AssetsTab ventureId={product.ventureId} productId={product.id} onChanged={onReload} />
        )}
        {tab === "documents" && (
          <DocumentsTab ventureId={product.ventureId} productId={product.id} />
        )}
        {tab === "revisions" && (
          <RevisionsTab ventureId={product.ventureId} productId={product.id} />
        )}
        {tab === "approval" && (
          <ApprovalTab
            ventureId={product.ventureId}
            productId={product.id}
            approval={product.approval}
            onChanged={onReload}
          />
        )}
      </div>
    </Card>
  );
}

function ROField({ label, value, testId }: { label: string; value?: string | null; testId: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</div>
      <div className="px-3 py-2 text-sm rounded"
        style={{ background: "var(--ph-surface-alt)", border: "1px solid var(--ph-border-subtle)", minHeight: "38px", color: value ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)" }}
        data-testid={testId}>
        {value || "Not yet enriched"}
      </div>
    </div>
  );
}

// ── Create modal ─────────────────────────────────────────────────
function CreateProductModal({
  ventureId, onClose, onCreated,
}: { ventureId: string; onClose: () => void; onCreated: (id: string) => void | Promise<void>; }) {
  const [code, setCode] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!code.trim()) { setErr("Product code is required."); return; }
    setBusy(true); setErr(null);
    try {
      const created = await productMaster.create(ventureId, {
        productCode: code.trim().toUpperCase(),
        nameEn: nameEn.trim() || code.trim().toUpperCase(),
      });
      await onCreated((created as any).id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" onClick={onClose} data-testid="create-modal">
      <Card className="w-full max-w-md" >
        <div onClick={(e) => e.stopPropagation()}>
        <SectionHeader eyebrow="Product Master" title="New product" />
        <div className="grid grid-cols-1 gap-3">
          <Field label="Product Code (unique)">
            <TextIn value={code} onChange={(v) => setCode(v.toUpperCase())} testId="create-code" />
          </Field>
          <Field label="Name (English)">
            <TextIn value={nameEn} onChange={setNameEn} testId="create-nameEn" />
          </Field>
        </div>
        {err && <div className="mt-3 text-xs text-red-600 flex items-center gap-1" data-testid="create-error"><AlertTriangle size={12} /> {err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} testId="create-cancel">Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy} testId="create-submit">
            <Plus size={14} /> {busy ? "Creating…" : "Create"}
          </Button>
        </div>
        </div>
      </Card>
    </div>
  );
}
